
import axios from 'axios';
import { Preferences as Storage } from '@capacitor/preferences';
import { environment } from '@/shared/env';

// ---------------------------------------------------------------------------
// Raw axios instance used ONLY for the token refresh call.
//
// Using the main `apiInterceptor` instance for refresh would:
//   1. Add the expired Bearer token to the Authorization header, causing some
//      servers to reject the refresh request with a new 401.
//   2. Run the response interceptor, which throws for non-success envelopes and
//      would swallow the new tokens if the server returns them unwrapped.
// ---------------------------------------------------------------------------
const _rawAxios = axios.create();

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const apiInterceptor = axios.create({});

// ---------------------------------------------------------------------------
// Store injection
//
// The auth store (Pinia) is injected from main.js immediately after
// `app.use(pinia)` — before any route navigation or API calls occur.
// This eliminates the circular-import between auth.ts → apiInterceptor → auth.ts
// that the previous lazy-require workaround tried to solve.
//
// Usage in main.js (after app.use(pinia)):
//   import { useAuthStore } from '@/stores';
//   import { setAuthStore } from '@/shared/apiInterceptor';
//   setAuthStore(useAuthStore());
// ---------------------------------------------------------------------------

/** @type {ReturnType<import('@/stores/auth').useAuthStore> | null} */
let _authStore = null;

/** Inject the Pinia auth store. Must be called before any authenticated requests. */
export const setAuthStore = (store) => {
  _authStore = store;
};

// ---------------------------------------------------------------------------
// Token refresh lock
//
// Prevents concurrent 401 handlers from each triggering their own refresh.
// When one handler starts a refresh, all subsequent 401s wait on the same
// promise instead of starting a second (doomed) refresh request.
// ---------------------------------------------------------------------------

/** Shared promise for an in-flight token refresh, or null when idle. */
let _refreshPromise = null;

// ---------------------------------------------------------------------------
// Request interceptor — attach auth token and device header
// ---------------------------------------------------------------------------

apiInterceptor.interceptors.request.use((config) => {
  config.headers['x-device'] = 'web';
  if (_authStore?.authData?.token) {
    config.headers['Authorization'] = `Bearer ${_authStore.authData.token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — unwrap envelope, surface metadata, bubble errors
// ---------------------------------------------------------------------------

/**
 * Attaches a non-enumerable property to an object.
 * Guards against primitives/null so Object.defineProperty never throws.
 */
const attachMeta = (target, key, value) => {
  if (target && typeof target === 'object') {
    Object.defineProperty(target, key, { value, enumerable: false, writable: true });
  }
};

apiInterceptor.interceptors.response.use(
  async (response) => {
    // Blob responses (file downloads) — return as-is.
    if (response.data instanceof Blob) return response.data;

    const { data } = response;
    const { success, status, message, error_message, errorMessage, next_cursor, pagination } = data;

    if (success || status === 'success') {
      const payload = data.data;

      if (payload && typeof payload === 'object') {
        // Attach envelope-level metadata as non-enumerable properties so
        // downstream code can access them without polluting Object.keys().
        attachMeta(payload, 'apiMessage', message || errorMessage);
        if (next_cursor) attachMeta(payload, 'next_cursor', next_cursor);
        if (pagination) attachMeta(payload, 'pagination', pagination);
      }

      // Return the inner payload when present; fall back to the full envelope.
      // Treat null the same as undefined — callers checking response.dialogMessage
      // rely on receiving the full envelope when data.data is null.
      return (payload !== undefined && payload !== null) ? payload : data;
    }

    // Non-success — surface the first meaningful error string.
    const errorMsg =
      [message, error_message, errorMessage].find((m) => typeof m === 'string' && m.trim()) ||
      'An error occurred.';
    throw new Error(errorMsg);
  },

  // ---------------------------------------------------------------------------
  // Error interceptor — network failures, 401 refresh cycle, 422 messages
  // ---------------------------------------------------------------------------
  async (error) => {
    // Prefer a server-provided error message over Axios' generic string.
    if (error?.response?.data?.errorMessage?.length) {
      error.message = error.response.data.errorMessage;
    }

    const { isAxiosError, response } = error;

    if (!response) {
      // No HTTP response — network error, CORS failure, or interceptor threw.
      const msg =
        error?.message && error.message !== 'Network Error'
          ? error.message
          : 'Network error — please check your connection and try again.';
      throw new Error(msg);
    }

    if (!isAxiosError) throw error;

    const { status } = response;

    switch (status) {
      case 401: {
        // The refresh token itself is invalid — clear session and redirect to login.
        // This only fires if some code path calls the refresh endpoint via
        // apiInterceptor (e.g. jwtInterceptor or legacy code). The main lock
        // path uses _rawAxios, so it never reaches here.
        if (error.config.url?.endsWith('auth/token/refresh/')) {
          console.info('[apiInterceptor] Refresh token invalid, logging out user.');
          _refreshPromise = null;
          await Storage.clear();
          location.href = '/login';
          return;
        }

        // MFA verification errors must propagate to the caller.
        if (error.config.url?.endsWith('auth/token/mfa/verify')) {
          throw error;
        }

        // If the original request carried no Authorization header the user was
        // never authenticated in the first place (e.g. a TanStack Query that
        // fires on the login page before tokens are loaded). Attempting a
        // refresh or redirect here would create an infinite reload loop in
        // incognito / unauthenticated sessions — just propagate the error.
        if (!error.config.headers?.['Authorization']) {
          throw error;
        }

        // No refresh token available — clear session and redirect to login.
        // Read from Storage as the authoritative source because
        // _authStore.authData may not be populated yet (e.g. if a TanStack
        // Query fires before loadStorageTokens() completes on first load).
        const refreshToken =
          _authStore?.authData?.refreshToken ||
          (await Storage.get({ key: 'refresh_token' })).value ||
          null;

        if (!refreshToken) {
          console.info('[apiInterceptor] No refresh token present, redirecting to login.');
          await Storage.clear();
          location.href = '/login';
          return;
        }

        // Acquire the refresh lock: if a refresh is already in flight, wait for
        // it rather than firing a second concurrent refresh (which would fail
        // with token rotation and cause a spurious reload loop).
        if (!_refreshPromise) {
          _refreshPromise = _rawAxios
            .post(
              environment('VUE_APP_API_URL') + 'auth/token/refresh/',
              { refresh: refreshToken },
              { headers: { 'x-device': 'web' } }
              // Intentionally no Authorization header — the refresh endpoint only
              // needs the refresh token in the body; sending an expired Bearer
              // token in the header can cause the server to reject the request.
            )
            .then(async (rawResponse) => {
              // The refresh endpoint may return tokens either inside a `data`
              // envelope ({ success: true, data: { access, refresh } }) or at
              // the root level ({ access, refresh }).  Handle both shapes.
              const body = rawResponse.data ?? {};
              const newAccess = body?.data?.access ?? body?.access ?? null;

              if (!newAccess) {
                console.error('[apiInterceptor] Token refresh returned no access token.', body);
                await Storage.clear();
                location.href = '/login';
                return null;
              }

              if (_authStore) {
                await _authStore.refreshAccessToken({ access: newAccess });
              } else {
                // Store not yet injected — persist the token directly so the
                // retried request can pick it up from Storage.
                console.warn('[apiInterceptor] _authStore not set; writing token to Storage directly.');
                await Storage.set({ key: 'access_token', value: newAccess });
              }
              return newAccess;
            })
            .catch(async (refreshError) => {
              console.error('[apiInterceptor] Token refresh failed.', refreshError);
              await Storage.clear();
              location.href = '/login';
              return null;
            })
            .finally(() => {
              _refreshPromise = null;
            });
        }

        const newAccessToken = await _refreshPromise;
        if (newAccessToken) {
          error.config.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return apiInterceptor(error.config);
        }
        // Refresh failed — error already handled (redirect) inside the promise.
        return;
      }

      case 422: {
        const d = response.data;
        if (typeof d?.errorMessage === 'string') throw new Error(d.errorMessage);
        if (typeof d?.data === 'object' && Object.keys(d.data).length) throw new Error(JSON.stringify(d.data));
        if (typeof d?.message === 'string') throw new Error(d.message);
        throw new Error('Something went wrong');
      }

      default:
        throw error;
    }
  }
);

export default apiInterceptor;
